import _ from 'lodash';
import { useEffect } from 'react';
import { KeyRecoilProps } from '@/shared/model';
import { DEVICE_TAB_TYPES } from '@/shared/config';
import {
  selectDeviceEventData,
  selectEventStripsData,
  selectActiveTab,
  useReportStore,
} from '@/entities/report';

/**
 * Tracks whether the strips management tab currently has anything to show. On every change of active button or strip data it filters the device event list down to entries matching the active button to decide isEmptyTab, additionally flags isEmptyAutoEvents when no AI-captured strips exist, and writes the result to the report store's empty-selected-key state.
 *
 * @param keyRecoil - Report store record key identifying which tab's slice of state this effect drives.
 * @returns Nothing; this hook only runs the emptiness-tracking effect.
 */
const useTabStripManagementUIEffect = ({ keyRecoil }: KeyRecoilProps) => {
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const eventStripsData = useReportStore(selectEventStripsData(keyRecoil));
  const deviceEventData = useReportStore(selectDeviceEventData(keyRecoil));
  const setEmptySelectedKeyAction = useReportStore((s) => s.actions.setEmptySelectedKey);
  /**
   * Records which parts of the strips management tab came back empty. Forwards the flags to the report store's setEmptySelectedKey action together with the tab's record key.
   *
   * @param v - Emptiness flags to store, such as isEmptyTab and isEmptyAutoEvents.
   * @returns Whatever the store's setEmptySelectedKey action returns, normally nothing.
   */
  const setEmptySelectedKey = (v: Parameters<typeof setEmptySelectedKeyAction>[1]) =>
    setEmptySelectedKeyAction(keyRecoil, v);

  useEffect(() => {
    const isDeviceEvent = DEVICE_TAB_TYPES.includes(activeButton as any);
    const config: { isEmptyTab: boolean; isEmptyAutoEvents?: boolean } = { isEmptyTab: false };
    if (isDeviceEvent) {
      const filteredData = _.filter(
        (deviceEventData as any)?.[activeButton],
        (x: any) => x.filterType === activeButton,
      );
      config.isEmptyTab = _.isEmpty(filteredData);
    }
    if (_.isEmpty(eventStripsData)) {
      config.isEmptyAutoEvents = true;
    }
    setEmptySelectedKey(config);
  }, [activeButton, eventStripsData, deviceEventData]);
};

export default useTabStripManagementUIEffect;
